export const OFFERED_PLACEMENT_LEVELS = ['A2', 'B1', 'B2', 'C1'];

const QUESTIONS_PER_LEVEL = 15;
const PASS_THRESHOLD = 0.75;
const A2_READY_THRESHOLD = 0.5;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function createEmptyLevelStats() {
  return {
    asked: 0,
    correct: 0,
    accuracy: null,
    weightedAccuracy: null,
    grammarAsked: 0,
    grammarCorrect: 0,
    grammarAccuracy: null,
  };
}

function buildEmptyPlacementStats() {
  return {
    A2: createEmptyLevelStats(),
    B1: createEmptyLevelStats(),
    B2: createEmptyLevelStats(),
    C1: createEmptyLevelStats(),
  };
}

function getLevelQuestionCount(questionBank, level) {
  return Math.min(QUESTIONS_PER_LEVEL, questionBank[level]?.length ?? 0);
}

function getLevelAnswers(answers, level) {
  return answers.filter((answer) => answer.level === level);
}

function getLevelCorrectAnswers(answers, level) {
  return getLevelAnswers(answers, level).filter((answer) => answer.isCorrect).length;
}

function summarizePlacementAnswers(answers) {
  const levelStats = buildEmptyPlacementStats();
  let correctAnswers = 0;

  for (const answer of answers) {
    const stats = levelStats[answer.level];
    if (!stats) {
      continue;
    }

    stats.asked += 1;
    if (answer.isCorrect) {
      stats.correct += 1;
      correctAnswers += 1;
    }

    if (answer.questionType === 'grammar') {
      stats.grammarAsked += 1;
      if (answer.isCorrect) {
        stats.grammarCorrect += 1;
      }
    }
  }

  for (const level of OFFERED_PLACEMENT_LEVELS) {
    const stats = levelStats[level];
    stats.accuracy = stats.asked > 0 ? stats.correct / stats.asked : null;
    stats.weightedAccuracy = stats.accuracy;
    stats.grammarAccuracy = stats.grammarAsked > 0 ? stats.grammarCorrect / stats.grammarAsked : null;
  }

  return {
    levelStats,
    correctAnswers,
    overallAccuracy: answers.length > 0 ? correctAnswers / answers.length : 0,
  };
}

function getLevelPassCount(questionCount, threshold) {
  if (threshold === PASS_THRESHOLD && questionCount >= 14) {
    return 11;
  }
  return Math.ceil(questionCount * threshold);
}

function getPlacementProgress(questionBank, answers, config) {
  for (const level of config.levelsInTest) {
    const questionCount = config.questionCountByLevel[level] ?? 0;
    const levelAnswers = getLevelAnswers(answers, level);

    if (levelAnswers.length < questionCount) {
      return {
        currentLevel: level,
        currentLevelQuestionCount: questionCount,
        shouldEnd: false,
      };
    }

    const correctAnswers = levelAnswers.filter((answer) => answer.isCorrect).length;
    const readyCount = getLevelPassCount(questionCount, config.a2ReadyThreshold);
    const passCount = getLevelPassCount(questionCount, config.passThreshold);

    if (level === 'A2' && correctAnswers < readyCount) {
      return {
        shouldEnd: true,
        stopLevel: level,
        finalLevel: 'A2',
        assessedLevel: 'A1',
        rangeStatus: 'not_ready',
      };
    }

    if (correctAnswers < passCount) {
      return {
        shouldEnd: true,
        stopLevel: level,
        finalLevel: level,
        assessedLevel: level,
        rangeStatus: 'in_range',
      };
    }
  }

  const highestLevel = config.levelsInTest[config.levelsInTest.length - 1] || 'A2';
  return {
    shouldEnd: true,
    stopLevel: highestLevel,
    finalLevel: highestLevel,
    assessedLevel: highestLevel,
    rangeStatus: 'in_range',
  };
}

function getFirstUnansweredQuestionIndex(questions, answers) {
  const askedQuestionIds = new Set(answers.map((answer) => answer.questionId));
  return questions.findIndex((question) => !askedQuestionIds.has(question.id));
}

export function createPlacementEngineConfig(languageCode, questionBank) {
  const levelsInTest = OFFERED_PLACEMENT_LEVELS.filter((level) => getLevelQuestionCount(questionBank, level) > 0);
  const questionCountByLevel = Object.fromEntries(
    levelsInTest.map((level) => [level, getLevelQuestionCount(questionBank, level)])
  );
  const maxQuestions = levelsInTest.reduce(
    (total, level) => total + (questionCountByLevel[level] ?? 0),
    0
  );

  return {
    languageCode,
    levelsInTest,
    offeredLevels: [...levelsInTest],
    questionsPerLevel: QUESTIONS_PER_LEVEL,
    questionCountByLevel,
    passThreshold: PASS_THRESHOLD,
    a2ReadyThreshold: A2_READY_THRESHOLD,
    maxQuestions,
  };
}

export function getNextPlacementQuestion(questionBank, answers, config) {
  const progress = getPlacementProgress(questionBank, answers, config);
  if (progress.shouldEnd || !progress.currentLevel) {
    return null;
  }

  const levelQuestions = (questionBank[progress.currentLevel] ?? []).slice(
    0,
    config.questionCountByLevel[progress.currentLevel] ?? 0
  );
  const questionIndex = getFirstUnansweredQuestionIndex(levelQuestions, answers);
  if (questionIndex < 0) {
    return null;
  }

  return {
    level: progress.currentLevel,
    questionIndex,
  };
}

export function shouldEndPlacementTest(questionBank, answers, config) {
  if (answers.length === 0) {
    return false;
  }

  return getPlacementProgress(questionBank, answers, config).shouldEnd;
}

export function calculatePlacementResult(answers, config) {
  const totalQuestions = answers.length;
  const { correctAnswers, overallAccuracy, levelStats } = summarizePlacementAnswers(answers);
  const progress = getPlacementProgress(
    Object.fromEntries(config.levelsInTest.map((level) => [level, Array(config.questionCountByLevel[level]).fill(null)])),
    answers,
    config
  );

  const finalLevel = progress.finalLevel || progress.currentLevel || config.levelsInTest[0] || 'A2';
  const assessedLevel = progress.assessedLevel || finalLevel;
  const rangeStatus = progress.rangeStatus || 'in_progress';
  const stopLevel = progress.stopLevel || finalLevel;
  const stopLevelStats = levelStats[stopLevel] || createEmptyLevelStats();
  const referenceThreshold =
    rangeStatus === 'not_ready' && stopLevel === 'A2'
      ? getLevelPassCount(config.questionCountByLevel[stopLevel] ?? totalQuestions, config.a2ReadyThreshold) /
        Math.max(config.questionCountByLevel[stopLevel] ?? totalQuestions, 1)
      : getLevelPassCount(config.questionCountByLevel[stopLevel] ?? totalQuestions, config.passThreshold) /
        Math.max(config.questionCountByLevel[stopLevel] ?? totalQuestions, 1);
  const margin = Math.abs((stopLevelStats.accuracy ?? overallAccuracy) - referenceThreshold);
  const confidence = clamp(0.55 + margin * 1.2, 0.35, 0.95);

  return {
    finalLevel,
    assessedLevel,
    rangeStatus,
    confidence,
    ability: null,
    uncertainty: null,
    totalQuestions,
    correctAnswers,
    rawAccuracy: overallAccuracy,
    overallAccuracy,
    weightedAccuracy: overallAccuracy,
    levelStats,
  };
}

export function getPlacementLevelSummary(questionBank, answers, config, level) {
  const questionCount = config.questionCountByLevel[level] ?? getLevelQuestionCount(questionBank, level);
  const correctAnswers = getLevelCorrectAnswers(answers, level);
  const accuracy = questionCount > 0 ? correctAnswers / questionCount : 0;

  return {
    level,
    questionCount,
    correctAnswers,
    accuracy,
    passCount: getLevelPassCount(questionCount, config.passThreshold),
    a2ReadyCount: level === 'A2' ? getLevelPassCount(questionCount, config.a2ReadyThreshold) : null,
  };
}
